import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/inventory/supplier-prices"
)({
  component: SupplierPricesPage,
});

import { useState } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { SupplierPriceFormSheet } from "@/components/inventory/forms/SupplierPriceFormSheet";
import { SupplierPriceTable } from "@/components/inventory/tables/SupplierPriceTable";
import {
  useFetchSupplierPrices,
  useSupplierPriceMutations,
} from "@/hooks/api/inventory/usePricing";
import type { SupplierPrice, SupplierPricePayload } from "@/types/inventory";

function SupplierPricesPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<SupplierPrice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SupplierPrice | null>(
    null
  );

  const supplierPricesQuery = useFetchSupplierPrices({
    page,
    page_size: pageSize,
  });
  const supplierPrices = supplierPricesQuery.data?.results ?? [];
  const count = supplierPricesQuery.data?.count ?? 0;

  const { createSupplierPrice, updateSupplierPrice, deleteSupplierPrice } =
    useSupplierPriceMutations();

  const isMutating =
    createSupplierPrice.isPending ||
    updateSupplierPrice.isPending ||
    deleteSupplierPrice.isPending;
  const isLoading = supplierPricesQuery.isPending || isMutating;
  const isFetching = supplierPricesQuery.isFetching || isMutating;

  const handleSubmit = async (data: SupplierPricePayload) => {
    if (editing) {
      await updateSupplierPrice.mutateAsync({
        id: editing.id,
        payload: data,
      });
      toast.success("Supplier price updated");
    } else {
      await createSupplierPrice.mutateAsync(data);
      toast.success("Supplier price added");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSupplierPrice.mutateAsync({ id: deleteTarget.id });
      toast.success("Supplier price deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Supplier Prices"
          description="Manage supplier purchase prices for inventory items"
          onAdd={() => {
            setEditing(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Supplier Price"
          isLoading={isFetching}
        />

        <SupplierPriceTable
          supplierPrices={supplierPrices}
          isLoading={isLoading}
          onView={(sp) => {
            setEditing(sp);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(sp) => {
            setEditing(sp);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(sp) => setDeleteTarget(sp)}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <SupplierPriceFormSheet
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
            setIsViewOnly(false);
          }}
          onSave={handleSubmit}
          items={[]}
          editing={editing}
          isLoading={
            createSupplierPrice.isPending || updateSupplierPrice.isPending
          }
          isViewOnly={isViewOnly}
        />

        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Supplier Price"
          description={`Delete this supplier price for "${deleteTarget?.item_name}"?`}
          itemName={deleteTarget?.item_name}
          isLoading={deleteSupplierPrice.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
